import { Auth0Provider } from '@auth0/auth0-react';
import { ChakraProvider, extendTheme, Box } from '@chakra-ui/react';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { CookiesProvider } from 'react-cookie';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BrowserRouter } from 'react-router-dom';

import { url as commonUrl } from '@finley-technologies/common';

import { GlobalProvider } from 'core/GlobalContext';
import { Router } from 'core/Router';
import theme from 'core/theme';
import { launchDarklyFeatureFlags } from 'packages/common-frontend/featureFlags';
import { useHeap } from 'packages/common-frontend/hooks/useHeap';
import { UnsafeDisableSandboxMode } from 'packages/core/SandboxMode';
import LoginRedirectPathProvider from 'packages/logged-out/hooks/LoginRedirectPathProvider';
import { SideBarContextProvider } from 'packages/sidebar/hooks/sideBarContext';

export const App = () => {
  /* Not unsafe here; this is setting a top-level default and providing the default ApolloClient. */
  return (
    <UnsafeDisableSandboxMode>
      <BrowserRouter>
        <DndProvider backend={HTML5Backend}>
          <Box>
            <Router />
          </Box>
        </DndProvider>
      </BrowserRouter>
    </UnsafeDisableSandboxMode>
  );
};

export const Entry = () => {
  useHeap(HEAP_APP_ID);

  const AppWithLDProvider = withLDProvider({
    clientSideID: LAUNCH_DARKLY_CLIENT_SIDE_ID,
    flags: launchDarklyFeatureFlags,
  })(App);

  // set global context here
  return (
    <Auth0Provider
      domain={commonUrl.getAuth0Domain()}
      clientId={commonUrl.getAuth0ClientId()}
      authorizationParams={{
        // eslint-disable-next-line camelcase
        redirect_uri: commonUrl.getAuth0CallbackUrl(),
        audience: `${commonUrl.getAuth0ApiUrl()}/`,
      }}
    >
      <CookiesProvider>
        <GlobalProvider>
          <LoginRedirectPathProvider>
            <SideBarContextProvider>
              <ChakraProvider theme={extendTheme(theme)}>
                <AppWithLDProvider />
              </ChakraProvider>
            </SideBarContextProvider>
          </LoginRedirectPathProvider>
        </GlobalProvider>
      </CookiesProvider>
    </Auth0Provider>
  );
};
